import axios from '@/libs/axios';
import { normalizarFaixas, precoPorFaixa } from '@/services/Operacoes/picFaixas';
import { aplicarPrecoDoacao } from '@/utils/impostoSimulador';
import { selecionarNaturezaFiscal } from '@/utils/naturezaFiscal';
import { precoFinalSimulado, precoTabelaSimulado } from '@/utils/precoProveniencia';

// Sem cache de parâmetro: uma troca de tenant não pode reutilizar o modo anterior.
export async function calcularImpostosPic(items, cliente, capa, endereco) {
  const { data: parametro } = await axios.get('/parametros/find', {
    params: { parametro: 'INTEGRACAO_TOTVS' },
  });
  const semIntegracao = parametro?.parametro_descricao === 'INTEGRACAO_TOTVS'
    && String(parametro.parametro_valor) === '0';
  if (!Array.isArray(items)) {
    throw new Error('Itens da promoção inválidos.');
  }
  const itens = items;
  if (!itens.length) return [];
  const naturezaInicial = selecionarNaturezaFiscal(cliente, { endereco });

  // Faixas são aplicadas sobre a quantidade total do mesmo produto na mesma
  // PIC. Uma PIC pode trazer o mesmo item em mais de uma cor/tamanho; nesse
  // caso todas as linhas precisam receber o mesmo preço unitário da faixa.
  const quantidadesPorPicItem = itens.reduce((totais, item) => {
    const itemId = item.itemid ?? item.itemId;
    const chave = `${item.picid ?? ''}:${itemId ?? ''}`;
    const quantidade = Number(item.picitemquantidade ?? item.pedidoItemQuantidade);
    if (!Number.isInteger(quantidade) || quantidade <= 0) {
      throw new Error('A promoção possui preço ou quantidade inválidos.');
    }
    totais[chave] = (totais[chave] || 0) + quantidade;
    return totais;
  }, {});

  const resultados = await Promise.all(itens.map(async (item, index) => {
    const itemId = item.itemid ?? item.itemId;
    const precoBaseInformado = item.picitemprecobase ?? item.picitemvalorpic
      ?? item.pedido_item_preco_unitario;
    const precoBase = Number(precoBaseInformado);
    const quantidade = Number(item.picitemquantidade ?? item.pedidoItemQuantidade);
    if (!Number.isFinite(precoBase) || precoBase <= 0 || !Number.isInteger(quantidade) || quantidade <= 0) {
      throw new Error('A promoção possui preço ou quantidade inválidos.');
    }

    const chavePicItem = `${item.picid ?? ''}:${itemId ?? ''}`;
    const quantidadeFaixaInformada = item.picitemquantidadetotal == null
      ? quantidadesPorPicItem[chavePicItem]
      : Number(item.picitemquantidadetotal);
    const quantidadeFaixa = Number(quantidadeFaixaInformada);
    if (!Number.isInteger(quantidadeFaixa) || quantidadeFaixa <= 0) {
      throw new Error(`A promoção possui quantidade inválida para o item ${itemId}.`);
    }
    const faixas = normalizarFaixas(item.picitemfaixas == null ? [] : item.picitemfaixas);
    const faixaCalculada = precoPorFaixa(precoBase, faixas, quantidadeFaixa);
    const preco = Number(faixaCalculada?.preco);
    if (!Number.isFinite(preco) || preco <= 0) {
      throw new Error(`Não foi possível calcular o preço da faixa do item ${itemId}.`);
    }
    const chaveItem = item.picitemchave || `${item.picid ?? ''}:${itemId ?? ''}:${item.picitemsequencia ?? index}`;

    if (semIntegracao) {
      return {
        itemid: itemId,
        picid: item.picid,
        picitemsequencia: item.picitemsequencia,
        picitemchave: chaveItem,
        precoUnitario: preco,
        desconto: faixaCalculada.desconto,
        faixa: faixaCalculada.faixa,
        precoComImposto: preco,
        baseIcms: 0,
        valorIcms: 0, naturezaoperacaoidimposto: naturezaInicial,
        semSimulacaoFiscal: true,
      };
    }
    const { data: resposta } = await axios.post('/imposto/simular', {
      estabelecimentoid: cliente.estabelecimento?.estabelecimentoid,
      clienteId: cliente.cliente?.clienteid,
      clienteNomeAbreviado: cliente.cliente?.clientenomeabreviado
        || cliente.cliente?.clientenomematriz || cliente.cliente?.clienteiderp,
      naturezaOperacaoId: naturezaInicial,
      canalVendaId: cliente.canalVenda?.canal_venda_id || '',
      itemId,
      quantidade,
      // O preço da faixa já inclui o desconto da PIC. O backend fiscal deve
      // simular esse preço final, sem reaplicar o desconto.
      precoUnitario: preco,
      ufDs: endereco?.ufid,
      cidadeDs: endereco?.clienteenderecocidade,
      contribuinteIcms: capa.clienteisentoie ? 0 : 1,
      tabelaPrecoId: capa.tabelaprecoid,
      condicaoPagamentoId: capa.condicaopagamentoid,
    });
    const imposto = aplicarPrecoDoacao(resposta?.data, String(capa.tipopedidoid || '').toUpperCase() === 'WD', capa.tipopedidoid);
    const valor = precoFinalSimulado(imposto);
    if (!imposto || imposto._erro || !Number.isFinite(valor) || valor <= 0) {
      throw new Error(`Não foi possível calcular os impostos do item ${itemId}. Os itens anteriores foram mantidos.`);
    }
    return {
      itemid: itemId,
      picid: item.picid,
      picitemsequencia: item.picitemsequencia,
      picitemchave: chaveItem,
      precoUnitario: preco,
      desconto: faixaCalculada.desconto,
      faixa: faixaCalculada.faixa,
      precoComImposto: valor,
      precoTabelaErp: precoTabelaSimulado(imposto),
      baseIcms: imposto.baseIcms || 0, valorIcms: imposto.valorIcms || 0,
      naturezaoperacaoidimposto: imposto.naturezaOperacao || '',
    };
  }));
  return resultados;
}